import React, { useCallback, useMemo, useState } from 'react'
import { App } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import { ItemTypes, MASKS_TYPE } from '../../../constants/constants'
import { isAnUrl } from '../../../utils/browser'
import { convertContentToRawText } from '../../RichEditor/utils'
import { transformTextToEditorState } from '../../../utils/dataSync'
import { reinitializeBoxWidthAndHeight } from '../../../utils/boxes'
import { isFixedImageFit } from '../../../shared/boxes/imageFit'
import { mutateInjectVariablesInDocument } from '../../../utils/variables'
import { mutateSearchAndLaunchTrigger } from '../../../utils/triggers'
import {
  appendRawContents,
  hydrateVariables,
  isEditorStateEmpty,
  rawContentHasEntity,
  rawContentToText,
} from '../../../utils/predefinedValues'
import ButtonCommon from '../../common/ButtonCommon'

// Le nom d'une box variable peut être une chaîne, une raw Draft.js sérialisée
// ou directement un EditorState (cas des box image) — transformTextToEditorState
// ne sait traiter que les deux premiers et renvoie du vide pour le troisième.
const getLabel = (variableBox, fallback) => {
  const { name } = variableBox
  if (variableBox.type === ItemTypes.CUSTOM_TEXT_VARIABLE) return name ? `@${name}` : fallback
  if (!name) return fallback
  if (typeof name === 'string') return name.trim() || fallback
  const text = typeof name.getCurrentContent === 'function'
    ? convertContentToRawText(name)
    : convertContentToRawText(transformTextToEditorState(name))
  return text.trim() || fallback
}

const TARGET_TYPES = [
  ItemTypes.CUSTOM_TEXT_VARIABLE,
  ItemTypes.IMG_VARIABLE,
  ItemTypes.TEXT_VARIABLE,
]

// Remplissage en masse : chaque variable qui a exactement une valeur prédéfinie
// et qui est encore vide la reçoit. Une variable déjà saisie est laissée telle
// quelle sans bruit (l'action reste rejouable sans rien écraser) ; seules les
// variables ambiguës — plusieurs valeurs possibles — sont listées à la fin.
const ApplyAllPredefinedValues = ({ formItems }) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const isTemplate = useStore(({ currentDocument }) => currentDocument?.type === 'template')
  const updateVariable = useStore(({ updateVariable }) => updateVariable)
  const updateBox = useStore(({ updateBox }) => updateBox)
  const bumpDocumentContentSyncTick = useStore(({ bumpDocumentContentSyncTick }) => bumpDocumentContentSyncTick)
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const [isApplying, setIsApplying] = useState(false)

  // Boxes du formulaire susceptibles d'avoir des valeurs prédéfinies, dans
  // l'ordre d'affichage et sans doublon.
  const targets = useMemo(() => {
    const seen = new Set()
    const list = []
    formItems.forEach(({ variablesBoxes }) => {
      (variablesBoxes ?? []).forEach((variableBox) => {
        if (seen.has(variableBox.id)) return
        if (!TARGET_TYPES.includes(variableBox.type)) return
        seen.add(variableBox.id)
        list.push(variableBox)
      })
    })
    return list
  }, [formItems])

  const hasPredefinedValues = useMemo(
    () => targets.some(({ predefinedValues }) => (predefinedValues ?? []).length > 0),
    [targets]
  )

  const showReport = useCallback((appliedCount, skipped) => {
    modal.info({
      title: t('apply-predefined-values-all'),
      centered: true,
      content: (
        <div>
          {/* `number` et pas `count` : `count` déclencherait la résolution des
              pluriels d'i18next sur des clés qui n'en ont pas. */}
          <div>{appliedCount > 0 ? t('predefined-values-applied', { number: appliedCount }) : t('predefined-values-none')}</div>
          {skipped.length > 0 &&
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600 }}>{t('predefined-values-skipped-title')}</div>
              <div style={{ fontSize: 12 }}>{t('predefined-values-skip-multiple')}</div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {skipped.map(({ id, label }) => <li key={id}>{label}</li>)}
              </ul>
            </div>
          }
        </div>
      ),
    })
  }, [modal, t])

  const handleClick = useCallback(async () => {
    setIsApplying(true)
    try {
      // Lecture à la volée : les valeurs saisies évoluent hors de ce composant.
      const { variables, boxes, masks } = useStore.getState()
      const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()]
      const skipped = []
      const textTargets = []
      const imageTargets = []
      const richTextTargets = []

      targets.forEach((variableBox) => {
        const label = getLabel(variableBox, t('unnamed-variable'))
        if (variableBox.type === ItemTypes.CUSTOM_TEXT_VARIABLE) {
          const variable = variables[variableBox.id]
          if (!variable) return
          const values = (variable.predefinedValues ?? []).filter((value) => value && value.trim() !== '')
          if (values.length === 0) return
          if (values.length > 1) {
            skipped.push({ id: variableBox.id, label })
            return
          }
          if ((variable.value ?? '').trim() !== '') return
          textTargets.push({ id: variableBox.id, value: values[0] })
          return
        }

        const box = allBoxes.find((b) => b.id === variableBox.id)
        if (!box) return

        if (variableBox.type === ItemTypes.TEXT_VARIABLE) {
          // Contenus riches : une valeur vide de texte peut n'être qu'une image.
          const rawValues = (box.predefinedValues ?? []).filter((value) =>
            typeof value === 'string' &&
            (rawContentToText(value) !== '' || rawContentHasEntity(value, 'IMAGE'))
          )
          if (rawValues.length === 0) return
          if (rawValues.length > 1) {
            skipped.push({ id: box.id, label })
            return
          }
          if (!isEditorStateEmpty(box.content?.editorState)) return
          richTextTargets.push({ id: box.id, rawValue: rawValues[0] })
          return
        }

        const values = (box.predefinedValues ?? []).filter((value) => !!value?.url)
        if (values.length === 0) return
        if (values.length > 1) {
          skipped.push({ id: box.id, label })
          return
        }
        // Une box image sans URL affiche le visuel d'attente : on la considère vide.
        if (isAnUrl(box.content?.src)) return
        imageTargets.push({ id: box.id, url: values[0].url })
      })

      textTargets.forEach(({ id, value }) => {
        updateVariable(id, (variable, draft) => {
          variable.value = value
          mutateSearchAndLaunchTrigger({ triggerId: id, draft })
          // draft.variables et pas le snapshot : plusieurs variables d'une même
          // box sont remplies dans la foulée, réinjecter d'anciennes valeurs
          // écraserait celles déjà appliquées.
          mutateInjectVariablesInDocument({ variables: draft.variables, variable, variableKey: id, draft })
        })
      })

      richTextTargets.forEach(({ id, rawValue }) => {
        updateBox(id, (b) => {
          const appended = appendRawContents({
            editorState: b.content?.editorState,
            rawValues: [rawValue],
          })
          if (!appended) return
          // Le texte figé dans la valeur est le nom des variables : sans cette
          // traduction il resterait affiché jusqu'au prochain chargement.
          const nextEditorState = hydrateVariables({ editorState: appended, variables })
          b.content.editorState = nextEditorState
          // Hauteur laissée telle quelle : une box texte la porte en pixels,
          // elle est remesurée à la déselection, comme après une saisie.
        })
      })

      // Les dimensions réelles donnent le ratio de la box ; un échec de
      // chargement ne doit pas empêcher de poser la source.
      const loadedImages = await Promise.all(imageTargets.map(({ id, url }) => new Promise((resolve) => {
        const imgEl = document.createElement('img')
        const done = () => resolve({ id, url, ratio: imgEl.width && imgEl.height ? imgEl.width / imgEl.height : null })
        imgEl.onload = done
        imgEl.onerror = done
        imgEl.src = url
      })))

      loadedImages.forEach(({ id, url, ratio }) => {
        updateBox(id, (b, column, columnWidth) => {
          b.content = { ...b.content, src: url, ...(ratio ? { ratio } : {}) }
          // Contenir / remplir : la box garde sa taille, seule l'image change.
          if (isFixedImageFit(b)) return
          b.height = 'auto'
          // Box de masque : updateBox passe `true` en 2e argument, pas une colonne.
          if (column && typeof column === 'object' && !column.drawMode) {
            b.width = reinitializeBoxWidthAndHeight({ header, footer, box: b, columnWidth }).width
          }
        })
      })

      // Les éditeurs des box gardent une copie locale de leur contenu tant
      // qu'ils sont sélectionnés : sans ce top, la copie périmée écraserait la
      // valeur appliquée en se sauvegardant.
      if (richTextTargets.length > 0) bumpDocumentContentSyncTick()

      showReport(textTargets.length + imageTargets.length + richTextTargets.length, skipped)
    } finally {
      setIsApplying(false)
    }
  }, [targets, updateVariable, updateBox, header, footer, showReport, bumpDocumentContentSyncTick, t])

  // Réservé aux propositions (décision produit du 18/10/2026).
  if (isTemplate || !hasPredefinedValues) return null

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 0 8px' }}>
      <ButtonCommon
        onClick={handleClick}
        loading={isApplying}
        disabled={isApplying}
        icon={<FontAwesomeIcon icon={icon({ name: 'list-check', style: 'solid' })} />}
      >
        {t('apply-predefined-values-all')}
      </ButtonCommon>
    </div>
  )
}

export default ApplyAllPredefinedValues
